import React from 'react'
import { Link } from 'react-router-dom'
import { Deal } from './crmData'
import { useCrm } from './useCrm'
import { getDealSupport } from './crmApi'

/**
 * Support tab (Phase 2.1) — the support history of this lead, joined live.
 *
 * Everything here already existed; it had simply never been connected. A ticket is
 * matched to a lead by the visitor's email (any contact on the deal, including the
 * secondary contact a merged cold lead brought with them), by the company alias the
 * widget captures from the WeQuote URL, or by wequote_account_id. Each row shows
 * HOW it matched, because an account-level match is stronger evidence than a match
 * on one person's email — and the churn model needs to know the difference.
 *
 * Nothing is copied into the CRM. These rows are read from the support tables at
 * render time, so a ticket closed in the Tickets inbox is correct here instantly.
 */

export interface SupportTicket { id: string; number: string; subject: string | null; status: string; source: string; days: number | null; matchedBy: string }
export interface SupportChat { id: string; status: string; channel: string | null; agent: string | null; days: number | null; summary: string | null; matchedBy: string }
export interface DealSupportData {
  matched: boolean
  identity: { emails: string[]; aliases: string[]; accountIds: string[] }
  account: { wequoteAccountId: string; planTier: string | null; quotaMins: number | null; quotaUsedMins: number | null } | null
  stats: { openTickets: number; ticketsTotal: number; lastTicketDays: number | null; chatsTotal: number; openChats: number; lastChatDays: number | null; lastContactDays: number | null }
  tickets: SupportTicket[]
  chats: SupportChat[]
}

const EMPTY: DealSupportData = {
  matched: false,
  identity: { emails: [], aliases: [], accountIds: [] },
  account: null,
  stats: { openTickets: 0, ticketsTotal: 0, lastTicketDays: null, chatsTotal: 0, openChats: 0, lastChatDays: null, lastContactDays: null },
  tickets: [],
  chats: [],
}

const TICKET_STATUS: Record<string, { label: string; bg: string; fg: string }> = {
  open: { label: 'Open', bg: '#FEE2E2', fg: '#991B1B' },
  answered: { label: 'Answered', bg: '#DBEAFE', fg: '#1E40AF' },
  closed: { label: 'Closed', bg: '#E2E8F0', fg: '#475569' },
}
const CHAT_STATUS: Record<string, { label: string; bg: string; fg: string }> = {
  queued: { label: 'Queued', bg: '#FEF3C7', fg: '#92400E' },
  open: { label: 'Open', bg: '#DCFCE7', fg: '#166534' },
  closed: { label: 'Closed', bg: '#E2E8F0', fg: '#475569' },
}

export const ago = (d: number | null) => (d == null ? '—' : d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d}d ago`)

const Pill: React.FC<{ map: Record<string, { label: string; bg: string; fg: string }>; value: string }> = ({ map, value }) => {
  const s = map[value] || { label: value, bg: '#E2E8F0', fg: '#475569' }
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide shrink-0" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
}

const Stat: React.FC<{ label: string; value: React.ReactNode; tone?: string }> = ({ label, value, tone }) => (
  <div className="flex-1 min-w-[104px] px-3 py-2.5 rounded-lg bg-surface-secondary">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">{label}</p>
    <p className="text-lg font-bold leading-tight mt-0.5 text-txt-primary" style={tone ? { color: tone } : undefined}>{value}</p>
  </div>
)

export const DealSupport: React.FC<{ deal: Deal }> = ({ deal }) => {
  const { data, live } = useCrm<DealSupportData>(() => getDealSupport(deal.id) as Promise<DealSupportData>, EMPTY, [deal.id])
  const s = data.stats

  return (
    <div className="max-w-[720px] space-y-5">
      {!live && <p className="text-[11px] text-txt-tertiary">Support history unavailable right now.</p>}

      <div className="flex gap-2 flex-wrap">
        <Stat label="Open tickets" value={s.openTickets} tone={s.openTickets >= 3 ? '#dc2626' : s.openTickets > 0 ? '#d97706' : undefined} />
        <Stat label="Tickets (all)" value={s.ticketsTotal} />
        <Stat label="Chats" value={s.chatsTotal} />
        <Stat label="Last contact" value={ago(s.lastContactDays)} />
      </div>

      {data.account && (
        <p className="text-[11px] text-txt-tertiary">
          Support account <span className="font-semibold text-txt-secondary">{data.account.wequoteAccountId}</span>
          {data.account.planTier ? ` · ${data.account.planTier}` : ''}
          {data.account.quotaMins != null ? ` · support minutes ${data.account.quotaUsedMins ?? 0}/${data.account.quotaMins}` : ''}
        </p>
      )}

      {!data.matched && (
        <div className="rounded-xl border border-border p-5">
          <p className="text-sm font-semibold text-txt-primary">No support history for this lead</p>
          <p className="text-xs text-txt-tertiary mt-1 leading-relaxed">
            Nothing matched on{' '}
            {[
              data.identity.emails.length ? `${data.identity.emails.length} contact email${data.identity.emails.length > 1 ? 's' : ''}` : null,
              data.identity.aliases.length ? 'the company alias' : null,
              data.identity.accountIds.length ? 'the WeQuote account id' : null,
            ].filter(Boolean).join(', ') || 'anything — this lead has no email, alias or account id we could match on'}
            . If they have contacted support, it will appear here as soon as one of those handles lines up.
          </p>
        </div>
      )}

      {data.tickets.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">Tickets</p>
          <div className="rounded-xl border border-border overflow-hidden">
            {data.tickets.map((t) => (
              <Link
                key={t.id}
                to={`/tickets/${t.id}`}
                className="flex items-center gap-2.5 px-4 py-3 border-b border-border last:border-0 hover:bg-surface-hover transition-colors"
              >
                <Pill map={TICKET_STATUS} value={t.status} />
                <span className="text-[12px] font-mono text-txt-tertiary shrink-0">{t.number}</span>
                <span className="text-[13px] text-txt-primary truncate flex-1">{t.subject || '(no subject)'}</span>
                <span className="text-[10px] text-txt-tertiary shrink-0" title={`matched by ${t.matchedBy}`}>{t.matchedBy}</span>
                <span className="text-[11px] text-txt-tertiary shrink-0 w-16 text-right">{ago(t.days)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {data.chats.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">Chats</p>
          <div className="rounded-xl border border-border overflow-hidden">
            {data.chats.map((c) => (
              <div key={c.id} className="flex items-center gap-2.5 px-4 py-3 border-b border-border last:border-0">
                <Pill map={CHAT_STATUS} value={c.status} />
                <span className="text-[11px] text-txt-tertiary shrink-0 w-16">{c.channel}</span>
                <span className="text-[13px] text-txt-secondary truncate flex-1">{c.summary || (c.agent ? `Handled by ${c.agent}` : 'No resolution note')}</span>
                <span className="text-[10px] text-txt-tertiary shrink-0" title={`matched by ${c.matchedBy}`}>{c.matchedBy}</span>
                <span className="text-[11px] text-txt-tertiary shrink-0 w-16 text-right">{ago(c.days)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
